import React, { useState } from "react";
import { useLanguage } from "@/i18n/LanguageContext";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { X } from "lucide-react";
import { OrnamentDivider } from "@/components/Ornament";
import { PageHeaderBanner } from "@/components/PageHeaderBanner";
import { ScrollReveal } from "@/components/ScrollReveal";
import { mixPhotos } from "@/data/mixPhotos";

// Span pattern repeats every 5 tiles to create an editorial mosaic
// regardless of total photo count (works from 1 photo to 100+).
const SPAN_PATTERN = [
  { span: "md:col-span-7 md:row-span-2", aspect: "aspect-[4/3] md:aspect-auto" },
  { span: "md:col-span-5", aspect: "aspect-[5/4]" },
  { span: "md:col-span-5", aspect: "aspect-[5/4]" },
  { span: "md:col-span-4", aspect: "aspect-square" },
  { span: "md:col-span-8", aspect: "aspect-[16/9]" },
];

const Gallery = () => {
  const { t } = useLanguage();
  const [active, setActive] = useState(null);

  const tiles = mixPhotos.map((img, i) => ({
    ...img,
    ...SPAN_PATTERN[i % SPAN_PATTERN.length],
  }));

  return (
    <div data-testid="gallery-page" className="pb-24 bg-sky-roze paper-grain relative overflow-hidden">
      <PageHeaderBanner
        testid="gallery-header-banner"
        eyebrow={t("gallery.eyebrow")}
        title={t("gallery.title")}
        lead={t("gallery.lead")}
      />

      <section className="container-roze relative pt-16 md:pt-24">
        <OrnamentDivider className="mb-14" />

        <div className="grid grid-cols-1 md:grid-cols-12 gap-3 md:gap-4">
          {tiles.map((img, idx) => (
            <ScrollReveal
              key={img.src}
              delay={(idx % 3) + 1}
              as="button"
              type="button"
              data-testid={`gallery-item-${idx}`}
              onClick={() => setActive(img)}
              className={`${img.span} ${img.aspect} group overflow-hidden relative bg-muted`}
            >
              <img
                src={img.src}
                alt={img.alt}
                loading="lazy"
                className="w-full h-full object-cover group-hover:scale-[1.06] transition-transform duration-1000"
              />
              <div className="absolute inset-0 bg-foreground/0 group-hover:bg-foreground/15 transition-colors" />
            </ScrollReveal>
          ))}
        </div>
      </section>

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent
          data-testid="gallery-lightbox"
          className="max-w-5xl p-0 bg-transparent border-0 shadow-none"
        >
          <DialogTitle className="sr-only">
            {active?.alt || "Gallery image"}
          </DialogTitle>
          {active && (
            <div className="relative">
              <button
                type="button"
                data-testid="gallery-lightbox-close"
                onClick={() => setActive(null)}
                className="absolute -top-12 right-0 text-white hover:text-white/80 transition-colors flex items-center gap-2 text-xs uppercase tracking-[0.22em]"
                aria-label="Close lightbox"
              >
                Close <X className="h-4 w-4" />
              </button>
              <img
                src={active.src}
                alt={active.alt}
                className="w-full h-auto max-h-[85vh] object-contain"
              />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Gallery;
